import { forwardRef, memo, useImperativeHandle, useMemo, type Dispatch, type SetStateAction } from 'react';
import router, { useRouter } from 'next/router';
import { Reorder, AnimatePresence } from 'framer-motion';

import Loader from '@atoms/Atom/Loader';
import Image from '@atoms/Image';
import useFonts from '@lib/hooks/useFonts';
import useNotificationTimer from '@lib/hooks/useNotificationTimer ';
import useFontSizeCalculator from '@components/Presentation/helpers/fontSizeCalculator';
import { defaultColor } from '@components/Presentation/constants';
import { type Interaction } from '@components/Presentation/types';

import LeaderboardItem from './LeaderboardItem';
import { useLeaderboard, mergeLeaders } from './Leaderboard/useLeaderboard';
import type { LeaderboardRowColors } from './Leaderboard/LeaderboardRow';
import type { LeaderboardFilter, LeaderboardRowData } from './Leaderboard/types';

export interface LeaderboardHandle {
  stopCurrentFeature: () => void;
}

interface LeaderboardAnalyticsData {
  id?: string;
  rows?: LeaderboardRowData[];
  color?: LeaderboardRowColors;
  settings?: { FontStyle?: string; quizImageSrc?: string | null };
}

interface LeaderboardProps {
  showingLeaderboard?: boolean;
  streamId?: string;
  currentRosFeature?: Partial<Interaction>;
  setShowNotification?: Dispatch<SetStateAction<boolean>>;
  isSandbox?: boolean;
  isAnalytics?: boolean;
  analyticsData?: LeaderboardAnalyticsData;
}

const EmptyBoard = () => (
  <div className="absolute m-0 top-1/2 left-1/2 transform -translate-y-1/2 -translate-x-1/2">
    <div className="flex flex-col items-center">
      <Image src="/images/chat.svg" />
      <div className="mt-8">
        <div className="flex justify-center items-center">
          <span className="font-medium text-white text-center text-lg leading-6">Nobody has commented</span>
        </div>
        <div className="flex justify-center items-center">
          <span className="font-medium text-white text-center text-lg leading-6">yet in the chat</span>
        </div>
      </div>
    </div>
  </div>
);

/** The frozen post-stream board rendered by the analytics result view. */
const LeaderboardAnalytics = ({ analyticsData }: { analyticsData?: LeaderboardAnalyticsData }) => {
  const { getFontStyle } = useFonts();
  const { fontFamily } = getFontStyle(analyticsData?.settings?.FontStyle || 'Sans-Serif');

  // Analytics rows carry raw scores only, so derive the bar widths here — the
  // same recompute the live board applies across pages.
  const leaders = useMemo(() => mergeLeaders(analyticsData?.rows ?? []), [analyticsData?.rows]);

  const quizImage = analyticsData?.settings?.quizImageSrc;
  const quizImagePath = quizImage ? `${process.env.NEXT_PUBLIC_BUCKET_URL}${quizImage}` : undefined;

  return (
    <div className="flex w-full h-full relative">
      <div
        id="scrollableDiv"
        style={{
          cursor: 'default',
          fontFamily,
          backgroundImage: quizImagePath ? `url(${quizImagePath})` : undefined,
        }}
        className="flex-1 scrollable-container max-w-6xl mx-auto flex flex-col pb-4 overflow-y-auto! relative p-4"
      >
        <Reorder.Group
          axis="y"
          values={leaders.map((leader) => leader.id)}
          onReorder={() => { }}
          className="space-y-4"
          layout
        >
          <AnimatePresence>
            <LeaderboardItem leaderboard={leaders} colors={analyticsData?.color} fontFamily={fontFamily} />
          </AnimatePresence>
        </Reorder.Group>
      </div>
    </div>
  );
};

const Leaderboard = forwardRef<LeaderboardHandle, LeaderboardProps>(({
  showingLeaderboard = false,
  streamId,
  currentRosFeature,
  setShowNotification,
  isSandbox = false,
  isAnalytics = false,
  analyticsData,
}, ref) => {
  const joinType = useRouter().query?.joinType;

  const filter = (currentRosFeature?.setting?.leaderboardShowWinners
    || 'all') as LeaderboardFilter;
  const colors = currentRosFeature?.color || defaultColor;

  const { leaders, sentinelRef, isLoading, hasNextPage } = useLeaderboard({
    streamId,
    featureId: currentRosFeature?.id,
    filter,
    questionNumber: currentRosFeature?.question_number,
    enabled: showingLeaderboard,
  });

  useNotificationTimer(currentRosFeature, leaders, setShowNotification);

  const { getFontStyle } = useFonts();
  const { fontFamily } = getFontStyle(currentRosFeature?.setting?.FontStyle || 'Sans-Serif');

  const computedFontSize = useFontSizeCalculator(
    currentRosFeature?.setting?.leaderboardFontSize
      ? Number(currentRosFeature.setting.leaderboardFontSize)
      : 1.2,
    1.8,
    'rem',
  );

  const stopCurrentFeature = () => {
    router.push({ pathname: `/streams/${streamId}/ros`, query: joinType ? { joinType } : {} });
  };

  useImperativeHandle(ref, () => ({ stopCurrentFeature }));

  // Hooks above run unconditionally — the analytics board is a different data
  // source, not a different component tree.
  if (isAnalytics) {
    return <LeaderboardAnalytics analyticsData={analyticsData} />;
  }

  return (
    <div className="flex w-full h-full relative">
      <div
        id="scrollableDiv"
        style={{
          cursor: 'default',
          fontFamily,
          ...(isSandbox && { height: 'calc(100vh - 400px)' }),
        }}
        className="flex-1 scrollable-container no-scrollbar max-w-6xl mx-auto flex flex-col pb-4 overflow-y-auto!"
      >
        {isLoading && <Loader showMessages={false} />}

        {!isLoading && leaders.length > 0 && (
          <>
            <Reorder.Group
              axis="y"
              values={leaders.map((leader) => leader.id)}
              onReorder={() => { }}
              className="space-y-4"
              layout
            >
              <AnimatePresence>
                <LeaderboardItem
                  leaderboard={leaders}
                  colors={colors}
                  fontSize={computedFontSize}
                  fontFamily={fontFamily}
                />
              </AnimatePresence>
            </Reorder.Group>
            {hasNextPage && <div ref={sentinelRef} style={{ height: 1 }} />}
          </>
        )}

        {!isLoading && leaders.length === 0 && <EmptyBoard />}
      </div>
    </div>
  );
});

Leaderboard.displayName = 'Leaderboard';

export default memo(Leaderboard);
